// FieldIQ — Door-knock enrollment + voter voice message flows

import { useState, useEffect, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Send,
  Shield,
  MapPin,
  ChevronRight,
  ChevronLeft,
  MessageCircle,
  Calendar,
  Trash2,
  Mic,
  Play,
  QrCode,
  RefreshCw,
  Check,
  Zap,
  X,
  Volume2,
} from "lucide-react";
import { authFetch } from "../lib/api";
import { queueOfflineWrite } from "../lib/offline";
import {
  SLUG_EMOJI,
  VOICE_MAX_SECONDS,
  VOICE_SUBMIT_ERROR,
  VOICE_TOPICS,
  WAVEFORM_HEIGHTS,
  fmtVoiceTime,
  submitVoiceMessage,
  useVoiceRecorder,
} from "../lib/voice";

// The auth service serves the public self-enroll page at /e/<token> on the app
// domain (same host that serves the /s/c/ survey page).
const PUBLIC_ENROLL_BASE = "https://app.hyveappliedintelligence.com";

const ENROLL_TOPICS_DEFAULT = [
  { label: "Water Rights", active: true },
  { label: "Tax Reform", active: true },
  { label: "Border Security", active: false },
];

export const DoorKnockEnrollFlow = ({
  onDone,
  address = "1247 N. 36th St — PC-04-12",
  facility,
}) => {
  const [step, setStep] = useState(0); // 0=QR, 1=waiting, 2=confirmed
  const [topics, setTopics] = useState(ENROLL_TOPICS_DEFAULT);
  const [adding, setAdding] = useState(false);
  const [addText, setAddText] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [timeLeft, setTimeLeft] = useState(272); // 4:32
  const [enrolled, setEnrolled] = useState(null);
  const [voterName, setVoterName] = useState("");
  const [voterPhone, setVoterPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [token, setToken] = useState(null); // self-enroll share token
  const [enrollUrl, setEnrollUrl] = useState(""); // real QR target
  const [minting, setMinting] = useState(false);

  // Mint a single-use self-enroll token → the real QR target (/e/<token>). The
  // enrollment the voter submits is attributed to this canvasser server-side.
  const mintToken = useCallback(async () => {
    setMinting(true);
    try {
      const data = await authFetch("/fieldiq/enroll-share-token", {
        method: "POST",
        body: JSON.stringify({
          facility_id: facility?.facility_id || null,
          address: address || null,
        }),
      });
      if (data?.token) {
        setToken(data.token);
        setEnrollUrl(`${PUBLIC_ENROLL_BASE}/e/${data.token}`);
        if (data.expires_at) {
          const secs = Math.round((new Date(data.expires_at).getTime() - Date.now()) / 1000);
          setTimeLeft(Math.max(0, secs));
        }
      }
    } catch {
      /* offline / error — QR stays in its generating state, manual entry still works */
    } finally {
      setMinting(false);
    }
  }, [facility, address]);

  // Mint once when the flow opens.
  useEffect(() => {
    mintToken();
  }, [mintToken]);

  // While waiting (step 1), poll for the voter's self-enroll and advance on submit.
  useEffect(() => {
    if (step !== 1 || !token) return;
    let alive = true;
    const poll = async () => {
      try {
        const data = await authFetch(
          `/fieldiq/enroll-share-token/status?token=${encodeURIComponent(token)}`,
        );
        if (alive && data?.used) {
          const nm = data.enrolled?.name || "Voter";
          const initials = nm.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
          setEnrolled({
            name: nm,
            initials,
            phone: data.enrolled?.phone || null,
            interests: topics.filter((t) => t.active).map((t) => t.label),
            gradient: "from-emerald-400 to-teal-500",
          });
          setStep(2);
        }
      } catch {
        /* transient — keep polling */
      }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [step, token, topics]);

  useEffect(() => {
    const fid = facility?.facility_id || "";
    const qs = fid ? `?facility_id=${encodeURIComponent(fid)}` : "";
    authFetch(`/fieldiq/topics${qs}`)
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setTopics(
            data.map((t) => ({ label: t.label || t.slug, active: true })),
          );
        }
      })
      .catch(() => {});
  }, [facility]);

  // QR expiry countdown
  useEffect(() => {
    if (step !== 0) return;
    const t = setInterval(() => setTimeLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [step]);

  // Elapsed timer while waiting
  useEffect(() => {
    if (step !== 1) return;
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [step]);

  const fmtTime = (s) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const toggleTopic = (i) =>
    setTopics((prev) =>
      prev.map((t, idx) => (idx === i ? { ...t, active: !t.active } : t)),
    );

  const addTopic = () => {
    const label = addText.trim();
    if (!label) return;
    setTopics((prev) => [...prev, { label, active: true }]);
    setAddText("");
    setAdding(false);
  };

  const handleEnroll = async () => {
    const name = voterName.trim();
    if (!name || submitting) return;
    setSubmitting(true);
    const activeTopics = topics.filter((t) => t.active).map((t) => t.label);
    const enrollBody = JSON.stringify({
      name,
      phone: voterPhone.trim() || null,
      address: address || null,
      facility_id: facility?.facility_id || null,
      enrolled_via: "direct",
      notes: activeTopics.length ? `Topics: ${activeTopics.join(", ")}` : null,
    });
    if (!navigator.onLine) {
      await queueOfflineWrite("/fieldiq/enroll-voter", "POST", enrollBody);
    } else {
      try {
        await authFetch("/fieldiq/enroll-voter", {
          method: "POST",
          body: enrollBody,
        });
      } catch {}
    }
    const initials = name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
    setEnrolled({
      name,
      initials,
      phone: voterPhone.trim() || null,
      interests: activeTopics,
      gradient: "from-emerald-400 to-teal-500",
    });
    setSubmitting(false);
    setStep(2);
  };

  // ── STEP 0 — Canvasser shows QR ──
  if (step === 0) {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={onDone}
            className="w-9 h-9 rounded-full bg-slate-800/60 border border-slate-700 flex items-center justify-center flex-shrink-0"
          >
            <ChevronLeft size={18} className="text-white" />
          </button>
          <div>
            <div className="text-slate-400 text-[10px] tracking-widest font-bold">
              CONNECT AT THE DOOR
            </div>
            <h1 className="text-white text-lg font-bold">
              Voter Self-Enrollment
            </h1>
          </div>
        </div>

        {/* Conversation context */}
        <div className="rounded-2xl p-4 bg-gradient-to-br from-cyan-500/15 via-blue-600/10 to-transparent border border-cyan-500/30">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle size={13} className="text-cyan-400" />
            <span className="text-cyan-300 text-[10px] font-bold tracking-widest">
              THIS CONVERSATION
            </span>
          </div>
          <div className="space-y-2 mb-3">
            <div className="flex items-start gap-2">
              <MapPin size={12} className="text-cyan-400 mt-0.5" />
              <div>
                <div className="text-slate-500 text-[10px] uppercase tracking-wider">
                  Location
                </div>
                <div className="text-white text-xs font-medium">{address}</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Calendar size={12} className="text-cyan-400 mt-0.5" />
              <div>
                <div className="text-slate-500 text-[10px] uppercase tracking-wider">
                  Started
                </div>
                <div className="text-white text-xs font-medium">Just now</div>
              </div>
            </div>
          </div>
          {/* Topic chips */}
          <div className="text-slate-500 text-[10px] uppercase tracking-wider mb-2">
            Topics discussed
          </div>
          <div className="flex flex-wrap gap-1.5">
            {topics.map((t, i) => (
              <button
                key={i}
                onClick={() => toggleTopic(i)}
                className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                  t.active
                    ? "bg-cyan-500/20 text-cyan-300 border-cyan-500/40"
                    : "bg-slate-800/40 text-slate-500 border-slate-700/50"
                }`}
              >
                {t.label}
              </button>
            ))}
            {adding ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  addTopic();
                }}
                className="flex items-center gap-1"
              >
                <input
                  autoFocus
                  value={addText}
                  onChange={(e) => setAddText(e.target.value)}
                  placeholder="Topic…"
                  className="text-[10px] px-2 py-1 rounded-full border border-cyan-500/40 bg-slate-900 text-white placeholder-slate-500 focus:outline-none w-20"
                />
                <button
                  type="submit"
                  className="text-cyan-400 text-[10px] font-bold"
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => setAdding(false)}
                  className="text-slate-500 text-[10px]"
                >
                  <X size={10} />
                </button>
              </form>
            ) : (
              <button
                onClick={() => setAdding(true)}
                className="text-[10px] font-semibold px-2.5 py-1 rounded-full border border-dashed border-slate-600 text-slate-400"
              >
                + Add
              </button>
            )}
          </div>
        </div>

        {/* QR code — real single-use self-enroll link (/e/<token>) */}
        <div className="bg-white rounded-2xl p-5 flex flex-col items-center">
          <div className="w-48 h-48 flex items-center justify-center">
            {enrollUrl ? (
              <QRCodeSVG value={enrollUrl} size={192} level="M" className="w-full h-full" />
            ) : (
              <div className="text-slate-400 text-xs">
                {minting ? "Generating…" : "QR unavailable — enroll manually"}
              </div>
            )}
          </div>
          <div className="mt-3 text-center">
            <div className="text-slate-900 font-bold text-sm break-all px-2">
              {enrollUrl ? enrollUrl.replace(/^https?:\/\//, "") : "—"}
            </div>
            <div className="text-slate-500 text-xs mt-0.5">
              {enrollUrl ? `Expires in ${fmtTime(timeLeft)}` : "Single-use link"}
            </div>
          </div>
        </div>

        {/* Script card */}
        <div className="bg-slate-800/40 rounded-xl p-3.5 border border-slate-700/50">
          <div className="text-white text-sm font-semibold mb-1.5">
            Show this to the voter
          </div>
          <div className="text-slate-300 text-xs leading-relaxed">
            "If you'd like me to follow up on what we talked about, scan this
            with your phone camera. You can share as much or as little as you
            want."
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <button
            onClick={mintToken}
            disabled={minting}
            className="flex-1 py-3 rounded-xl bg-slate-800/60 border border-slate-700/50 text-slate-200 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <RefreshCw size={14} />
            Regenerate
          </button>
          <button
            onClick={() => setStep(1)}
            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold flex items-center justify-center gap-2"
          >
            <QrCode size={14} />
            Voter Scanning…
          </button>
        </div>
      </div>
    );
  }

  // ── STEP 1 — Waiting for voter to submit ──
  if (step === 1) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setStep(0)}
            className="w-9 h-9 rounded-full bg-slate-800/60 border border-slate-700 flex items-center justify-center flex-shrink-0"
          >
            <ChevronLeft size={18} className="text-white" />
          </button>
          <div>
            <div className="text-slate-400 text-[10px] tracking-widest font-bold">
              WAITING
            </div>
            <h1 className="text-white text-lg font-bold">
              Voter is filling out…
            </h1>
          </div>
        </div>

        {/* Voter form preview (read-only illustration) */}
        <div className="rounded-2xl overflow-hidden border border-slate-700">
          {/* Form header in light theme illustration */}
          <div className="bg-gradient-to-br from-cyan-500 to-blue-600 px-4 pt-4 pb-6">
            <div className="text-white text-sm font-bold leading-snug">
              Hi! Your canvasser stopped by today.
            </div>
            <div className="text-white/80 text-xs mt-1">
              Share your info to receive follow-ups on topics you care about.
            </div>
          </div>
          {/* Simulated form fields */}
          <div className="bg-slate-50 px-4 py-3 space-y-2">
            {["First name", "Last name", "Mobile phone", "ZIP code"].map(
              (f) => (
                <div key={f} className="flex items-center justify-between">
                  <span className="text-slate-500 text-xs">{f}</span>
                  <div className="h-2.5 bg-slate-200 rounded-full w-24 animate-pulse" />
                </div>
              ),
            )}
          </div>
          <div className="bg-slate-100 px-4 py-2 text-[10px] text-slate-500 text-center border-t border-slate-200">
            Voter completing form on their device
          </div>
        </div>

        {/* Elapsed */}
        <div className="flex items-center justify-center gap-2 py-3">
          <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
          <span className="text-slate-400 text-sm">
            Waiting — {fmtTime(elapsed)} elapsed
          </span>
        </div>

        {/* Canvasser direct-entry */}
        <div className="space-y-2">
          <input
            value={voterName}
            onChange={(e) => setVoterName(e.target.value)}
            placeholder="Voter name (required)"
            className="w-full px-3.5 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
          />
          <input
            value={voterPhone}
            onChange={(e) => setVoterPhone(e.target.value)}
            placeholder="Phone (optional)"
            className="w-full px-3.5 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
          />
        </div>
        <button
          onClick={handleEnroll}
          disabled={!voterName.trim() || submitting}
          className="w-full py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Check size={16} strokeWidth={3} />
          {submitting ? "Enrolling…" : "Enroll Voter"}
        </button>
        <button
          onClick={onDone}
          className="w-full py-2.5 rounded-xl bg-slate-800/60 border border-slate-700 text-slate-400 text-sm"
        >
          Cancel — no response
        </button>
      </div>
    );
  }

  // ── STEP 2 — Confirmation ──
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
          <Check size={18} className="text-emerald-300" strokeWidth={3} />
        </div>
        <div>
          <div className="text-emerald-400 text-[10px] tracking-widest font-bold">
            CONNECTION COMPLETE
          </div>
          <h1 className="text-white text-lg font-bold">
            {enrolled?.name?.split(" ")[0]} just connected!
          </h1>
        </div>
      </div>

      {/* Success hero */}
      <div className="rounded-2xl p-5 bg-gradient-to-br from-emerald-500/20 via-cyan-500/10 to-transparent border border-emerald-500/30">
        <div className="text-white font-bold text-base leading-tight">
          {enrolled?.name} is now in your network
        </div>
        <div className="text-slate-300 text-sm mt-1">
          Self-enrolled with full consent · just now
        </div>
      </div>

      {/* Voter card */}
      <div className="bg-slate-800/40 rounded-xl border border-slate-700 overflow-hidden">
        <div className="p-3.5 flex items-center gap-3 border-b border-slate-700/60">
          <div
            className={`w-11 h-11 rounded-full bg-gradient-to-br ${enrolled?.gradient} flex items-center justify-center flex-shrink-0`}
          >
            <span className="text-white font-bold text-sm">
              {enrolled?.initials}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-semibold text-sm">
              {enrolled?.name}
            </div>
            <div className="text-slate-400 text-xs">{enrolled?.phone}</div>
          </div>
          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300">
            ENROLLED
          </span>
        </div>
        <div className="p-3.5 space-y-2">
          {[
            { label: "District", value: "LD4 · PC-04-12" },
            {
              label: "Vote history",
              value: "Voted 4 of last 4",
              highlight: true,
            },
            { label: "Propensity", value: "High", highlight: true },
          ].map(({ label, value, highlight }) => (
            <div key={label} className="flex items-center justify-between">
              <span className="text-slate-400 text-xs">{label}</span>
              <span
                className={`text-xs font-semibold ${highlight ? "text-cyan-300" : "text-white"}`}
              >
                {value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Interests */}
      <div>
        <div className="text-slate-400 text-[10px] uppercase tracking-widest font-bold mb-2">
          {enrolled?.name?.split(" ")[0]}'s interests
        </div>
        <div className="flex flex-wrap gap-1.5">
          {enrolled?.interests?.map((label) => (
            <span
              key={label}
              className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30"
            >
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* Suggested next action */}
      <div className="rounded-xl bg-gradient-to-br from-cyan-500/15 to-blue-600/5 border border-cyan-500/30 p-3.5">
        <div className="flex items-center gap-2 mb-1.5">
          <Zap size={14} className="text-cyan-400" />
          <span className="text-cyan-300 text-xs font-semibold">
            Send a thank-you text now
          </span>
        </div>
        <div className="text-slate-300 text-xs leading-relaxed mb-3">
          Following up within 5 minutes increases engagement 3×. Want a draft?
        </div>
        <a
          href={`sms:${enrolled?.phone?.replace(/\D/g, "")}`}
          className="w-full py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-bold flex items-center justify-center gap-2"
        >
          <Send size={14} />
          Draft thank-you text
        </a>
      </div>

      {/* Done */}
      <button
        onClick={onDone}
        className="w-full py-3 rounded-xl bg-slate-800/60 border border-slate-700 text-slate-200 text-sm font-semibold"
      >
        Done — back to canvassing
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// VOTER VOICE FLOW
// 5-step: pitch → record → topic tag → identity → confirmation
// ═══════════════════════════════════════════════════════════════════

export const VoterVoiceFlow = ({ onDone, facility, user }) => {
  const [step, setStep] = useState(1);
  const [selectedTopics, setSelectedTopics] = useState(new Set());
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [voiceTopicList, setVoiceTopicList] = useState(VOICE_TOPICS);

  useEffect(() => {
    const fid = facility?.facility_id || "";
    const qs = fid ? `?facility_id=${encodeURIComponent(fid)}` : "";
    authFetch(`/fieldiq/topics${qs}`)
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setVoiceTopicList(
            data.map((t) => ({
              icon: SLUG_EMOJI[t.slug] || "📋",
              label: t.label || t.slug,
            })),
          );
        }
      })
      .catch(() => {});
  }, [facility]);

  const {
    elapsed,
    audioBlob,
    start: startRecorder,
    stop: stopRecorder,
    reset: resetRecorder,
  } = useVoiceRecorder(() => setStep(3));

  const startRecording = async () => {
    setSubmitError(null);
    if (await startRecorder()) setStep(2);
  };

  const stopRecording = () => {
    stopRecorder();
    setStep(3);
  };

  const resetRecording = () => {
    resetRecorder();
    setStep(1);
  };

  const toggleTopic = (label) =>
    setSelectedTopics((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });

  const fmtTime = fmtVoiceTime;

  const submitVoice = async (isAnon) => {
    setAnonymous(isAnon);
    setSubmitting(true);
    setSubmitError(null);
    const result = await submitVoiceMessage({
      audioBlob,
      facility,
      isAnon,
      name,
      phone,
      selectedTopics,
      elapsed,
    });
    setSubmitting(false);
    if (result.ok) setStep(5);
    else setSubmitError(VOICE_SUBMIT_ERROR);
  };

  // ── STEP 1: Pitch ──
  if (step === 1) {
    return (
      <div className="space-y-4">
        <button
          onClick={onDone}
          className="flex items-center gap-1.5 text-sm text-cyan-400 hover:text-cyan-300"
        >
          <ChevronLeft size={16} /> Activities
        </button>

        <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-[#17253F] via-[#1F2F4D] to-[#17253F] border border-white/10">
          <div className="px-5 pt-6 pb-2">
            <div className="flex items-center gap-2 mb-6 opacity-90">
              <div className="w-6 h-6 border border-white/40 rounded flex items-center justify-center">
                <svg
                  viewBox="0 0 36 36"
                  className="w-3.5 h-3.5"
                  stroke="white"
                  fill="none"
                  strokeWidth="1.5"
                >
                  <polygon points="18,3 31,10.5 31,25.5 18,33 5,25.5 5,10.5" />
                  <polygon points="18,11 24.5,14.75 24.5,22.25 18,26 11.5,22.25 11.5,14.75" />
                </svg>
              </div>
              <div className="text-white/70 text-[9px] tracking-[0.16em] font-semibold">
                LD4 GOP · VOTER VOICE
              </div>
            </div>

            <div className="text-[#F97316] text-xs font-bold tracking-[0.2em] uppercase mb-2">
              Your Voice. Heard.
            </div>
            <h2 className="text-white text-2xl font-bold leading-tight mb-3">
              Tell our leaders what you actually think.
            </h2>
            <p className="text-[#BDD1F0] text-sm leading-relaxed mb-5">
              A 30-second message goes directly to LD4 leadership and your
              elected representatives — in your own words.
            </p>

            <div className="flex justify-center my-4">
              <div className="w-20 h-20 rounded-full bg-white/5 border-2 border-white/15 flex items-center justify-center">
                <Mic size={36} className="text-white" strokeWidth={1.5} />
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 mb-5 space-y-2.5">
              {[
                "Read by LD4 chairs and elected reps",
                "Aggregated weekly into a leadership digest",
                "Never sold, never used for ads",
                "You can request deletion any time",
              ].map((text) => (
                <div key={text} className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-[#F97316]/20 border border-[#F97316]/40 flex items-center justify-center flex-shrink-0">
                    <Check
                      size={9}
                      className="text-[#F97316]"
                      strokeWidth={3}
                    />
                  </div>
                  <span className="text-white text-xs">{text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="px-5 pb-5 space-y-2">
            <button
              onClick={startRecording}
              className="w-full py-3.5 rounded-xl bg-white text-[#17253F] font-bold text-sm flex items-center justify-center gap-2 shadow-lg"
            >
              <Mic size={16} /> Start Recording
            </button>
            <button
              onClick={onDone}
              className="w-full py-2.5 text-white/60 text-sm font-medium"
            >
              No thanks
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── STEP 2: Recording ──
  if (step === 2) {
    const remaining = VOICE_MAX_SECONDS - elapsed;
    return (
      <div className="space-y-4">
        <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-[#17253F] via-[#1A3163] to-[#17253F] border border-white/10">
          <div className="px-5 pt-6 pb-5">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-[11px] font-bold tracking-[0.16em] text-white/80 uppercase">
                  Recording
                </span>
              </div>
              <button
                onClick={resetRecording}
                className="text-white/60 text-xs font-semibold"
              >
                Cancel
              </button>
            </div>

            <div className="text-center mb-6">
              <div className="text-[#F97316] text-xs font-bold tracking-[0.2em] uppercase mb-2">
                Now speaking to leadership
              </div>
              <h3 className="text-white text-lg font-bold leading-tight px-2">
                Speak naturally. Tell them what's on your mind.
              </h3>
            </div>

            <div className="flex justify-center mb-5">
              <div className="relative">
                <div
                  className="absolute inset-0 rounded-full bg-[#F97316]/20 animate-ping"
                  style={{ animationDuration: "1.5s" }}
                />
                <div className="relative w-28 h-28 rounded-full bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center shadow-2xl">
                  <Mic size={48} className="text-white" strokeWidth={2} />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-0.5 h-10 mb-4">
              {WAVEFORM_HEIGHTS.map((h, i) => (
                <div
                  key={i}
                  className="w-1 rounded-full bg-white/70"
                  style={{
                    height: `${h}px`,
                    animation: "voiceWave 1s ease-in-out infinite",
                    animationDelay: `${i * 0.05}s`,
                  }}
                />
              ))}
            </div>
            <style>{`@keyframes voiceWave{0%,100%{transform:scaleY(0.4);opacity:0.5}50%{transform:scaleY(1);opacity:1}}`}</style>

            <div className="text-center mb-5">
              <div className="font-mono text-white text-3xl font-bold tracking-wider">
                {fmtTime(elapsed)}
              </div>
              <div className="text-white/50 text-xs mt-1">
                {remaining} seconds remaining
              </div>
              <div className="mt-2 w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-full bg-[#F97316] rounded-full transition-all"
                  style={{
                    width: `${(elapsed / VOICE_MAX_SECONDS) * 100}%`,
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <button
                onClick={stopRecording}
                className="w-full py-3.5 rounded-xl bg-white text-[#17253F] font-bold text-sm flex items-center justify-center gap-2"
              >
                <Check size={16} /> Done — Send My Message
              </button>
              <button
                onClick={resetRecording}
                className="w-full py-3 rounded-xl bg-white/10 border border-white/20 text-white text-sm font-semibold flex items-center justify-center gap-2"
              >
                <Trash2 size={13} /> Start Over
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── STEP 3: Topic tagging ──
  if (step === 3) {
    return (
      <div className="space-y-4">
        <button
          onClick={startRecording}
          className="flex items-center gap-1.5 text-sm text-cyan-400 hover:text-cyan-300"
        >
          <ChevronLeft size={16} /> Re-record
        </button>

        <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-[#17253F] via-[#1F2F4D] to-[#17253F] border border-white/10">
          <div className="px-5 pt-5 pb-5">
            <div className="text-[10px] font-bold text-[#BDD1F0] tracking-[0.16em] uppercase mb-1">
              Step 2 of 3
            </div>
            <h3 className="text-white text-lg font-bold mb-4">
              What's this about?
            </h3>

            <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 mb-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F97316] flex items-center justify-center flex-shrink-0">
                <Play size={14} className="text-white ml-0.5" fill="white" />
              </div>
              <div className="flex-1">
                <div className="text-white text-sm font-semibold">
                  Your message
                </div>
                <div className="text-white/60 text-xs">
                  {fmtTime(elapsed)} · Tap to listen back
                </div>
              </div>
              <button
                onClick={resetRecording}
                className="w-8 h-8 rounded-full bg-white/5 border border-white/15 flex items-center justify-center"
              >
                <Trash2 size={12} className="text-white/70" />
              </button>
            </div>

            <p className="text-[#BDD1F0] text-xs leading-relaxed mb-4">
              Tag the topics you talked about so leadership can route your
              message to the right people.
            </p>

            <div className="grid grid-cols-2 gap-2 mb-5">
              {voiceTopicList.map((t) => {
                const sel = selectedTopics.has(t.label);
                return (
                  <button
                    key={t.label}
                    onClick={() => toggleTopic(t.label)}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      sel
                        ? "bg-[#F97316]/15 border-[#F97316]/50"
                        : "bg-white/5 border-white/10"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-base">{t.icon}</span>
                      <span className="text-white text-xs font-semibold flex-1">
                        {t.label}
                      </span>
                      {sel && (
                        <div className="w-4 h-4 rounded-full bg-[#F97316] flex items-center justify-center flex-shrink-0">
                          <Check
                            size={9}
                            className="text-white"
                            strokeWidth={3}
                          />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setStep(4)}
              className="w-full py-3.5 rounded-xl bg-white text-[#17253F] font-bold text-sm flex items-center justify-center gap-2"
            >
              Continue <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── STEP 4: Identity ──
  if (step === 4) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => setStep(3)}
          className="flex items-center gap-1.5 text-sm text-cyan-400 hover:text-cyan-300"
        >
          <ChevronLeft size={16} /> Back
        </button>

        <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-[#17253F] via-[#1F2F4D] to-[#17253F] border border-white/10">
          <div className="px-5 pt-5 pb-5 space-y-4">
            <div>
              <div className="text-[10px] font-bold text-[#BDD1F0] tracking-[0.16em] uppercase mb-1">
                Step 3 of 3
              </div>
              <h3 className="text-white text-lg font-bold">Want a response?</h3>
            </div>

            <div>
              <p className="text-white text-sm leading-relaxed mb-1">
                Leave your name and number if you'd like leadership to follow
                up.
              </p>
              <p className="text-[#BDD1F0] text-xs leading-relaxed">
                Anonymous messages are welcome too — they still get heard, just
                no follow-up.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <div className="text-[#BDD1F0] text-[10px] font-semibold uppercase tracking-wider mb-1">
                  First name
                </div>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full px-3.5 py-3 rounded-lg bg-white/5 border border-white/15 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#F97316]/50"
                />
              </div>
              <div>
                <div className="text-[#BDD1F0] text-[10px] font-semibold uppercase tracking-wider mb-1">
                  Phone (optional)
                </div>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(602) 555-0000"
                  className="w-full px-3.5 py-3 rounded-lg bg-white/5 border border-white/15 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#F97316]/50"
                />
              </div>
            </div>

            <div className="bg-[#F97316]/10 border border-[#F97316]/30 rounded-xl p-3.5 flex items-start gap-2.5">
              <Shield
                size={14}
                className="text-[#F97316] flex-shrink-0 mt-0.5"
              />
              <div className="text-[#BDD1F0] text-[11px] leading-relaxed">
                Your contact info goes only to LD4 leadership for follow-up.
                Never sold. Never used for fundraising without separate
                permission.
              </div>
            </div>

            {submitError && (
              <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-3.5 text-red-300 text-xs leading-relaxed">
                {submitError}
              </div>
            )}

            <button
              onClick={() => submitVoice(false)}
              disabled={submitting}
              className="w-full py-3.5 rounded-xl bg-white text-[#17253F] font-bold text-sm flex items-center justify-center gap-2 shadow-lg disabled:opacity-60"
            >
              {submitting ? (
                "Sending…"
              ) : (
                <>
                  <Send size={15} /> Send to Leadership
                </>
              )}
            </button>
            <button
              onClick={() => submitVoice(true)}
              disabled={submitting}
              className="w-full py-2.5 text-white/60 text-sm font-medium text-center disabled:opacity-40"
            >
              {submitting ? "Sending…" : "Send Anonymously"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── STEP 5: Confirmation ──
  return (
    <div className="space-y-4">
      <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-[#17253F] via-[#1A3163] to-[#17253F] border border-white/10">
        <div className="px-5 pt-8 pb-5 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center shadow-2xl">
              <Check size={40} className="text-white" strokeWidth={3} />
            </div>
          </div>

          <div className="text-[#F97316] text-xs font-bold tracking-[0.2em] uppercase mb-2">
            Message Delivered
          </div>
          <h2 className="text-white text-2xl font-bold mb-3">
            {anonymous || !name ? "Thank you." : `Thank you, ${name}.`}
          </h2>
          <p className="text-[#BDD1F0] text-sm leading-relaxed mb-5 px-2">
            Your voice is now part of the LD4 leadership digest.
            {!anonymous && name && " You'll hear back within 7 days."}
          </p>

          {selectedTopics.size > 0 && (
            <div className="flex flex-wrap gap-1.5 justify-center mb-5">
              {[...selectedTopics].map((label) => (
                <span
                  key={label}
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#F97316]/15 text-[#F97316] border border-[#F97316]/30"
                >
                  {label}
                </span>
              ))}
            </div>
          )}

          <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-left mb-5">
            <div className="text-[10px] font-bold text-[#BDD1F0] tracking-[0.16em] uppercase mb-3">
              What happens next
            </div>
            {[
              "Your message reaches the LD4 chair this week",
              "Reps representing PC-04-12 receive a digest",
              ...(!anonymous && name
                ? ["You'll get a personal acknowledgment within 7 days"]
                : []),
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-3 mb-2.5 last:mb-0">
                <div className="w-5 h-5 rounded-full bg-[#F97316]/20 border border-[#F97316]/40 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#F97316] text-[9px] font-bold">
                    {i + 1}
                  </span>
                </div>
                <span className="text-white text-xs leading-relaxed">
                  {text}
                </span>
              </div>
            ))}
          </div>

          <button
            onClick={onDone}
            className="w-full py-3 rounded-xl bg-white/10 border border-white/20 text-white text-sm font-semibold flex items-center justify-center gap-2 mb-2"
          >
            <Volume2 size={14} /> Listen to my message
          </button>
          <button
            onClick={onDone}
            className="w-full py-2.5 text-white/60 text-sm"
          >
            Done — back to canvassing
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// ACTIVITIES TAB — events, meetings, and neighborhood walks
